import './learn/styles.css';
import { startGame } from './game/boot';
import { loadProgress, saveProgress, tickStreakOnLoad } from './shared/progress';
import { mountKpiConsole } from './learn/utils/kpiConsole';

mountKpiConsole();
import { runOnboarding } from './learn/onboarding';
import { renderHome } from './learn/home';
import { renderCover } from './learn/cover';
import { renderLearnPortal } from './learn/learn-portal';
import { renderTrainingHall } from './learn/training-hall';
import { renderLoadout } from './learn/loadout';
import { dispatchLesson } from './learn/lesson-route-table';
import { showUnlockCelebration } from './learn/unlock-celebration';
import { mountTouchControls } from './game/ui/touch-controls';
import { mountRotateHint } from './game/ui/rotate-hint';
import { mountFullscreenButton } from './game/ui/fullscreen-button';
import { mountViewportFit } from './game/ui/viewport-fit';
import { renderAcademy } from './learn/academy';
import { runExam } from './learn/exam-runner';
import { renderExamUpload } from './learn/exam-upload/upload';
import { runPhotoUpload } from './learn/review/photo-upload';
import { runRemediationLesson } from './learn/remediation';
import { renderParentDashboard } from './learn/parent-dashboard';
import { renderPrintableMode } from './learn/printable/printable-mode';
import type { RemediationMission } from './learn/remediation';
import { renderChapterList } from './learn/curriculum/chapter-detail';
import { renderChapterPage } from './learn/curriculum/chapter-page';
import { gradeSemesterOf } from './learn/grade-semester';
import type { Subject } from './shared/types';
import { enterAcademy, leaveAcademy } from './learn/learn-music';
import { renderArchiveQuiz } from './learn/archive-quiz';
import { renderMockExamPicker } from './learn/exam/mock-exam-picker';
import { renderMistakeBook } from './learn/mistake-book';
import { renderExamArchive } from './learn/review/exam-archive-view';
import { renderReviewHub } from './learn/review/review-hub';
import { runOralExam } from './learn/oral/oral-exam';
import { ORAL_EXAM_G3 } from './learn/oral/oral-exam-data';
import { runPracticeCenter } from './learn/review/bank-paper';
import { runExamPractice } from './learn/exam-bank/practice';
import { renderCampaignMap } from './learn/campaign-map';
import { renderLeaderboard } from './learn/leaderboard-local';
import { renderBossGallery } from './learn/boss-gallery';
import { renderProfilePicker } from './learn/profile-picker';
import { renderHeroSelect } from './learn/hero-select';
import { renderWeaponSelect } from './learn/weapon-select';
import { ensureFamilyState, listProfiles } from './shared/profile';
import { initAccountSync } from './shared/account';
import { startVersionCheck } from './shared/version-check';
import { purgeJunkProfiles, dedupeDuplicateProfiles } from './shared/cloud-sync';
import { stopActiveGameScenes } from './main/scene-router';
import { renderSettings } from './learn/settings';
import { lessonDoneRoute } from './main/lesson-route';
import { requestTutorialBossSpawn } from './game/scenes/horizontal-battle-scene';
import { completeTutorialBoss } from './learn/dayone-hook';
import { classifyMode, dispatchModeSwitch } from './game/scene-asset-lifecycle';
// 學習端入口整併(spec docs/spec/LEARN_IA_CONSOLIDATION.md「★ 定案」):四門路由。
import { runLearnDoor } from './learn/portal/learn-door';
import { runPracticeDoor } from './learn/portal/practice-door';
import { runReviewDoor } from './learn/portal/review-door';
// iOS 風統一導覽(boss 2026-06-14):集中式返回堆疊 + 全域左緣滑動返回 + 翻頁轉場。
import { resetBackStack } from './learn/nav/back-nav';
import { mountSwipeBack } from './learn/nav/swipe-back';
import { setLearnReduceMotion } from './learn/nav/motion-pref';
import { isReduceMotion } from './learn/settings';

type Mode = 'title' | 'onboarding' | 'tutorial' | 'learn-portal' | 'home' | 'academy' | 'academy-parent' | 'chapter-list' | 'chapter-page' | 'lesson' | 'exam' | 'exam-practice' | 'practice' | 'h-battle' | 'hub' | 'garage' | 'campaign-map' | 'hall' | 'loadout' | 'exam-upload' | 'photo-upload' | 'remediation' | 'dashboard' | 'printable' | 'archive-quiz' | 'mock-exam' | 'mistake-book' | 'exam-archive' | 'review-hub' | 'oral-exam' | 'ending' | 'campaign' | 'tower' | 'tower-leaderboard' | 'boss-gallery' | 'profile-picker' | 'settings' | 'hero-select' | 'weapon-select' | 'learn-door' | 'practice-door' | 'review-door';

const learnRoot = document.getElementById('learn-root')!;
const gameContainer = document.getElementById('game-container')!;
// W1「窮人版滿屏」level b:直持轉橫提示。掛一次即可 —— overlay 在
// #game-container 內,跟著 setMode 的 display 切換,只在遊戲模式可見;
// 顯示條件是純 CSS media query(portrait + 觸控),桌機永遠看不到。
mountRotateHint(gameContainer);
// 手機全螢幕入口(boss 2026-06-13「手機瀏覽器沒辦法全屏」)。掛一次即可,
// 跟著 #game-container display 切換,只在遊戲模式可見;iOS 走「加入主畫面」教學,
// Android/桌機走原生 Fullscreen API,已 standalone 則自動不顯示。
mountFullscreenButton(gameContainer);
let game: Phaser.Game | null = null;
// 手機全螢幕 overscan 根治(boss 2026-06-13):把 #game-container 尺寸鎖到
// visualViewport(真正可見區域),視口變化即重 fit。掛一次,getGame 回當前 game。
mountViewportFit(gameContainer, () => game);
let unmountTouch: (() => void) | null = null;
let currentSubject: Subject | null = null;
let currentChapter: number | null = null;
let currentMode: Mode | null = null;

function routeLessonDone(result: { newUnlocks: string[]; exited?: boolean }): void {
  const route = lessonDoneRoute(result, { hasChapter: !!(currentSubject && currentChapter) });
  if (route.kind === 'chapter-page') setMode('chapter-page');
  else if (route.kind === 'celebrate-then-home') showUnlockCelebration(learnRoot, route.newUnlocks, () => setMode('learn-portal'));
  else setMode('learn-portal');
}

function setMode(mode: Mode): void {
  if (unmountTouch) { unmountTouch(); unmountTouch = null; }
  // iOS 風統一導覽:硬切畫面(下面 learnRoot.innerHTML='')會丟掉舊畫面 DOM,但其
  // 推進返回堆疊的處理器不會自動移除 → 每次 setMode 先清空堆疊,讓新畫面的 pageShell
  // 重新 push 自己的返回處理器,維持「堆疊深度 = 可見畫面數」不變式(左緣滑動 / ‹ 才準)。
  resetBackStack();
  // Stop academy BGM when leaving academy
  if (currentMode === 'academy' && mode !== 'academy') {
    leaveAcademy();
  }
  // P05: fire ef:mode-switch so cross-mode unload handlers (學習端 ↔ 戰鬥端)
  // can release the *opposite* mode's BGM / sprite. Only fires on actual
  // learn↔battle transitions so DOM-internal hops (home → academy) stay cheap.
  const fromBucket = currentMode ? classifyMode(currentMode) : null;
  const toBucket = classifyMode(mode);
  if (fromBucket !== toBucket) {
    dispatchModeSwitch(fromBucket, toBucket);
  }
  currentMode = mode;

  learnRoot.innerHTML = '';
  // title 不再是 isGameMode — 改用 DOM 學習封面 (renderCover),不起 Phaser TitleScene。
  const isGameMode = mode === 'h-battle' || mode === 'tutorial' || mode === 'ending' || mode === 'hub' || mode === 'garage' || mode === 'campaign-map';
  gameContainer.style.display = isGameMode ? 'block' : 'none';
  learnRoot.style.display = isGameMode ? 'none' : 'flex';

  // 沒進遊戲(學習模式)→ 停掉還在跑的遊戲場景,連同它的 BGM。
  // 否則 title/結束 場景只被隱藏 (display:none) 但 Phaser 仍在跑,
  // 其 BgmPlayer 收不到 shutdown,音樂會漏到學習側 (boss: 沒進遊戲不要開音樂)。
  if (!isGameMode && game) {
    stopActiveGameScenes(game);
  }

  if (mode === 'title') {
    // 學習封面 (DOM) — 老闆 2026-06-08:封面要「一看就知道是念書/學霸專用」,
    // 不要戰車/夜空。改渲染 renderCover,title 不再起 Phaser TitleScene
    // (!isGameMode → 前面已 stopActiveGameScenes 停掉殘留遊戲場景 + BGM)。
    const onStart = () => {
      // If multiple profiles exist, let the user pick before entering home
      const profiles = listProfiles();
      if (profiles.length > 1) {
        setMode('profile-picker');
        return;
      }
      const progress = loadProgress();
      // 登入後 landing 改落 learn-portal — 讓學習先呈現 (學習堡壘為首頁)。
      setMode(progress ? 'learn-portal' : 'onboarding');
    };
    renderCover(learnRoot, onStart);
  } else if (mode === 'ending') {
    if (!game) game = startGame();
    if (game.scene.isActive('BootScene')) game.scene.stop('BootScene');
    stopActiveGameScenes(game);
    game.scene.start('EndingScene');
  } else if (mode === 'onboarding') {
    runOnboarding(learnRoot, () => {
      // 2026-05-29 boss decision: 「直接拿掉」tutorial — onboarding 結束直接進首頁。
      // landing 改 learn-portal,讓學習先呈現。
      setMode('learn-portal');
    });
  } else if (mode === 'tutorial') {
    if (!game) game = startGame();
    if (game.scene.isActive('BootScene')) game.scene.stop('BootScene');
    stopActiveGameScenes(game);
    game.scene.start('TutorialScene');
    // 2026-05-29 mobile-touch fix: TutorialScene step-0 forces 「按 ← 跟 →」
    // to advance, so mobile users need the on-screen ◀▶ buttons or the
    // tutorial dead-ends. Without this mount kid is stuck on the very
    // first step (reported by boss as 「Tutorial 進不去」).
    unmountTouch = mountTouchControls();
  } else if (mode === 'learn-portal') {
    // 學習堡壘 — 登入後唯一學習首頁(學習端入口整併,spec ★定案):
    // 4 個 tap target = 📖 學 / ✏️ 練 / 🔁 複習 + 📷 拍考卷(降一階)。
    // 學習與遊戲分離鐵則:遊戲收進底部「⚔️ 進入遊戲」按鈕後 → setMode('home')。
    renderLearnPortal(learnRoot, {
      onLearnDoor: () => setMode('learn-door'),
      onPracticeDoor: () => setMode('practice-door'),
      onReviewDoor: () => setMode('review-door'),
      // 📷 拍考卷:統一拍照上傳(extractAuto 自動分類考卷/單字)。
      onCapture: () => setMode('photo-upload'),
      onParentDashboard: () => setMode('dashboard'),
      onSettings: () => setMode('settings'),
      onEnterGame: () => setMode('home'),
    });
  } else if (mode === 'learn-door') {
    // 📖 學門:課程(看真課本單元)+ 英語口說。純學習側。
    runLearnDoor(learnRoot, {
      onCourses: () => setMode('academy'),
      onOral: () => setMode('oral-exam'),
      onBack: () => setMode('learn-portal'),
    });
  } else if (mode === 'practice-door') {
    // ✏️ 練門:自動出一張卷(出考卷中心)+ 計時模擬段考 + 更多卷種(真題/數學/聽力/
    // 自選範圍/變形)。吃掉 7 個舊出卷入口。純學習側,絕不碰 commanderXp。
    runPracticeDoor(
      learnRoot,
      () => setMode('learn-portal'),
      (c, onBack) => renderMockExamPicker(c, { onBack }),
    );
  } else if (mode === 'review-door') {
    // 🔁 複習門:第一眼我的錯題 + 今日到期 + 聽寫。出卷類已移到「練」。純學習側。
    runReviewDoor(
      learnRoot,
      () => setMode('learn-portal'),
      () => setMode('practice-door'),
    );
  } else if (mode === 'home') {
    // 2026-05-29 boss decision: 「直接拿掉」tutorial — 移除 force-tutorial gate。
    // LEARN_IA_CONSOLIDATION(boss 2026-06-14):遊戲首頁(home)只剩遊戲卡 —
    // 學科練習/考古題/複習中心/模擬考/錯題本/拍考卷批改全收進 learn-portal 4 門,
    // 避免兩個首頁重複讓家長迷路。學習用 mode/runner(archive-quiz/mock-exam/
    // mistake-book/review-hub/photo-upload…)由 learn-portal 4 門呼叫,此處不再傳。
    renderHome(learnRoot, {
      onBattle: () => setMode('loadout'),
      onTrainingHall: () => setMode('hall'),
      // ← 回學習堡壘:遊戲首頁回到 learn-portal 4 門。
      onBackToPortal: () => setMode('learn-portal'),
      // IA 改版(boss 2026-06-14):闖關地圖為唯一主入口。home「闖關」card 導向
      // Phaser CampaignMapScene(R-004 線性闖關),而非舊 DOM renderCampaignMap。
      onCampaign: () => setMode('campaign-map'),
      onTower: () => setMode('tower'),
      onTowerLeaderboard: () => setMode('tower-leaderboard'),
      onBossGallery: () => setMode('boss-gallery'),
      // garage-consolidation (boss 2026-06-15):首頁「選擇主角 / 選擇武器」兩張卡已移除 —
      // 選戰車 + 選武器一律在「我的車庫」(GarageScene)內做。故此處不再傳
      // onHeroSelect / onWeaponSelect。hero-select / weapon-select mode 仍保留:
      // weapon-select 由 loadout 出戰前「選主武器/升級」入口走;hero-select 畫面檔留著備用。
      // 車庫家(boss 2026-06-15):「進入戰場」CTA 改 navigate 到科技魔幻車庫 GarageScene
      // (進遊戲主 hub / 落點)— 大倉庫擺著「我的戰車收藏 + 武器架」,選出戰戰車後
      // 從車庫內按「⚔ 出戰 / 🗺 闖關 / 🔧 升級基地」進既有流程。學習與遊戲分離鐵則:
      // 車庫 0 題目,只 navigate;唯一寫入是 selectedHeroId(玩遊戲軌廣度)。
      onHBattle: () => setMode('garage'),
    });
  } else if (mode === 'academy') {
    enterAcademy();
    // Read parentMode flag from progress — toggled in settings 👪 家長管控.
    // When true, academy renders quantitative mastery bars instead of the
    // kid-friendly 「今天的章節」 placeholder.
    const academyProgress = loadProgress();
    const parentMode = academyProgress?.parentSettings?.parentMode === true;
    renderAcademy(learnRoot, {
      onSelectSubject: (subject) => {
        currentSubject = subject;
        setMode('chapter-list');
      },
      onSelectExam: () => setMode('exam'),
      onSelectRemediation: () => setMode('remediation'),
      onSelectPrintable: () => setMode('printable'),
      onBack: () => setMode('learn-portal'),
      parentMode,
    });
  } else if (mode === 'academy-parent') {
    // 林媽媽 2026-05-21 01:09 round-2 fix: dedicated parent-view of academy
    // (always parentMode=true regardless of parentSettings.parentMode flag).
    // Reached via the 「查看修煉學院 (家長視角)」 button in parent-dashboard.ts.
    enterAcademy();
    renderAcademy(learnRoot, {
      onSelectSubject: (subject) => {
        currentSubject = subject;
        setMode('chapter-list');
      },
      onSelectExam: () => setMode('exam'),
      onSelectRemediation: () => setMode('remediation'),
      onSelectPrintable: () => setMode('printable'),
      onBack: () => setMode('dashboard'),
      parentMode: true,
    });
  } else if (mode === 'chapter-list') {
    if (!currentSubject) { setMode('learn-portal'); return; }
    // 「學 → 看課本」只列當前學年單元(boss 2026-06-10);舊學年單元在「複習」。
    // 沿用 grade-semester.ts 單一真相讀當前學年(改設定頁學年 → 此清單即時跟著變)。
    const chapterGradeSemester = gradeSemesterOf(loadProgress());
    renderChapterList(learnRoot, currentSubject, {
      onSelectChapter: (subj, chNum) => {
        currentSubject = subj;
        currentChapter = chNum;
        setMode('chapter-page');
      },
      onBack: () => setMode('academy'),
    }, chapterGradeSemester);
  } else if (mode === 'chapter-page') {
    if (!currentSubject || !currentChapter) { setMode('learn-portal'); return; }
    renderChapterPage(learnRoot, currentSubject, currentChapter, {
      onSelectKP: (_subj, kpId) => {
        // Single dispatch path: stash kpId, switch to 'lesson' mode, the mode
        // handler below looks up KP_ROUTE and invokes the right runner. wip KPs
        // are filtered by the UI (isLessonWired) so this should always resolve.
        (window as any).__pendingKp = kpId;
        setMode('lesson');
      },
      onBack: () => setMode('chapter-list'),
    });
  } else if (mode === 'lesson') {
    const kpId = (window as any).__pendingKp as string | undefined;
    delete (window as any).__pendingKp;
    if (!kpId || !dispatchLesson(learnRoot, kpId, routeLessonDone)) {
      // Unknown / unwired KP — bounce back to chapter page rather than dead-end at home.
      setMode('chapter-page');
    }
  } else if (mode === 'exam') {
    runExam(learnRoot, () => setMode('learn-portal'));
  } else if (mode === 'exam-practice') {
    // 📝 真題練習 — 練習老師的段考真卷(自然/數學/英文文法 + 英文單字聽寫)。
    // 學習/遊戲分離鐵則:純學習側,絕不碰 commanderXp / 戰鬥數值。
    runExamPractice(learnRoot, () => setMode('learn-portal'));
  } else if (mode === 'practice') {
    // 📝 出練習卷中心 — 自選範圍出卷(全科題庫→A4 考卷→三鈕批改,boss 2026-06-11
    // 「都能產出可列印的 A4 考卷」)+ 除法餘數智慧卷(v2 閉環,保留)。
    // 學習/遊戲分離鐵則:純學習側,只動 factionXP(合法「學習=戰力」)。
    runPracticeCenter(learnRoot, () => setMode('learn-portal'));
  } else if (mode === 'hall') {
    renderTrainingHall(learnRoot, () => setMode('home'));
  } else if (mode === 'loadout') {
    // faction-select-fix (老闆「進去都是星算門」): loadout 完成改進 hub 選門派,
    // 而非直接 h-battle(會鎖死預設 celestial 星算門背景/魔王)。hub 內選門派 →
    // 帶 hubFactionId + bossId 進戰鬥,玩家才看得到五門派不同地圖。
    renderLoadout(learnRoot, () => setMode('hub'), () => setMode('home'));
  } else if (mode === 'exam-upload') {
    renderExamUpload(learnRoot, () => setMode('learn-portal'));
  } else if (mode === 'photo-upload') {
    // 📷 統一拍照上傳:一次多選照片,extractAuto 自動判斷考卷 vs 單字表,
    // 分別走家長確認(考卷→錯題本)/ addVocabBatch(單字→單字庫)。
    // 學習/遊戲分離鐵則:只動單字 / 弱點 / 錯題本,絕不碰 commanderXp。
    runPhotoUpload(learnRoot, () => setMode('learn-portal'));
  } else if (mode === 'remediation') {
    const mission = (window as unknown as { __pendingMission?: RemediationMission }).__pendingMission;
    if (!mission || !mission.phases) {
      setMode('learn-portal');
      return;
    }
    delete (window as unknown as { __pendingMission?: unknown }).__pendingMission;
    runRemediationLesson(learnRoot, mission, () => setMode('learn-portal'));
  } else if (mode === 'dashboard') {
    renderParentDashboard(learnRoot, () => setMode('learn-portal'));
  } else if (mode === 'printable') {
    renderPrintableMode(learnRoot, () => setMode('learn-portal'));
  } else if (mode === 'archive-quiz') {
    renderArchiveQuiz(learnRoot, { onBack: () => setMode('home') });
  } else if (mode === 'mock-exam') {
    renderMockExamPicker(learnRoot, { onBack: () => setMode('home') });
  } else if (mode === 'mistake-book') {
    renderMistakeBook(learnRoot, () => setMode('learn-portal'));
  } else if (mode === 'exam-archive') {
    // 📂 考卷紀錄 — 上傳批改過的整卷歸檔,隨時可出變形練習卷(答對的也不浪費)。
    // 學習/遊戲分離鐵則:純學習側,絕不碰 commanderXp / 戰鬥數值。
    renderExamArchive(learnRoot, () => setMode('learn-portal'));
  } else if (mode === 'review-hub') {
    renderReviewHub(learnRoot, () => setMode('learn-portal'));
  } else if (mode === 'oral-exam') {
    // 🎤 英語口試練習。學習/遊戲分離鐵則:純學習側,絕不碰 commanderXp / 戰鬥數值。
    runOralExam(learnRoot, ORAL_EXAM_G3, () => setMode('learn-portal'));
  } else if (mode === 'campaign') {
    renderCampaignMap(learnRoot, {
      onEnterBattle: (_nodeId, _kind) => {
        setMode('h-battle');
      },
      onBack: () => setMode('home'),
    });
  } else if (mode === 'h-battle') {
    if (!game) game = startGame();
    if (game.scene.isActive('BootScene')) game.scene.stop('BootScene');
    gameContainer.style.display = 'block';
    learnRoot.style.display = 'none';
    stopActiveGameScenes(game);
    game.scene.start('HorizontalBattleScene');
    unmountTouch = mountTouchControls();
  } else if (mode === 'garage') {
    // 科技魔幻「車庫家」(boss 2026-06-15)= 進遊戲的主 hub / 落點。大倉庫裡擺著
    // 「我已拿到的戰車/武器」,選出戰戰車(寫 selectedHeroId)、出戰、闖關、升級基地。
    // 學習與遊戲分離鐵則:GarageScene 0 題目,只 navigate;唯一寫入是 selectedHeroId
    // (玩遊戲軌廣度)。出戰/升級基地是 Phaser 內部 scene.start(不走 setMode):
    //   出戰 → 補發 ef:enter-battle 掛觸控;升級基地 → MetaShopScene(returnScene=GarageScene)。
    // 觸控控件不在這裡預掛(車庫是選單不是戰鬥,避免搖桿漏到車庫上;同闖關地圖的處理)。
    if (!game) game = startGame();
    if (game.scene.isActive('BootScene')) game.scene.stop('BootScene');
    gameContainer.style.display = 'block';
    learnRoot.style.display = 'none';
    stopActiveGameScenes(game);
    game.scene.start('GarageScene');
  } else if (mode === 'hub' || mode === 'campaign-map') {
    // IA 改版(boss 2026-06-14):闖關地圖(CampaignMapScene)= 唯一主入口。
    // 原「選門大廳」HubScene 已停用(orphan),門派改由每一關自帶主題/魔王
    // (關卡 def 的 hubFactionId)提供。'hub' 與 'campaign-map' 兩個 mode 名都
    // 導向 CampaignMapScene('hub' 保留是因為 onHBattle / loadout 完成 /
    // ef:return-hub 都走它)。學習與遊戲分離鐵則:闖關地圖 0 題目,只能 navigate
    // (點關卡 → HorizontalBattleScene / 「← 返回」→ home)。
    if (!game) game = startGame();
    if (game.scene.isActive('BootScene')) game.scene.stop('BootScene');
    gameContainer.style.display = 'block';
    learnRoot.style.display = 'none';
    stopActiveGameScenes(game);
    game.scene.start('CampaignMapScene');
    // touch-leak-fix (boss 2026-06-14「選門/地圖冒出搖桿+技能鈕,很醜」):
    // 闖關地圖是選單不是戰鬥 → 不預掛觸控控件。CampaignMapScene.onStageClick
    // 進戰鬥前已 dispatch 'ef:enter-battle'(見下方 listener),「真正開戰時」才掛載。
    // 不在這裡 mountTouchControls,否則搖桿會漏到闖關地圖上(同大廳那個 bug)。
  } else if (mode === 'tower') {
    if (!game) game = startGame();
    if (game.scene.isActive('BootScene')) game.scene.stop('BootScene');
    gameContainer.style.display = 'block';
    learnRoot.style.display = 'none';
    stopActiveGameScenes(game);
    game.scene.start('TowerScene');
    unmountTouch = mountTouchControls();
    // When run ends, return home
    const onTowerEnd = () => {
      window.removeEventListener('ef:tower-run-ended', onTowerEnd);
      // ef:return-home fires automatically from TowerScene._recordRun
    };
    window.addEventListener('ef:tower-run-ended', onTowerEnd);
  } else if (mode === 'tower-leaderboard') {
    renderLeaderboard(learnRoot, { onBack: () => setMode('home') });
  } else if (mode === 'boss-gallery') {
    renderBossGallery(learnRoot, () => setMode('home'));
  } else if (mode === 'profile-picker') {
    renderProfilePicker(learnRoot, {
      onPicked: () => {
        // After picking, run the normal boot flow (progress check → learn-portal or onboarding).
        // landing 改 learn-portal,讓學習先呈現。
        const p = loadProgress();
        setMode(p ? 'learn-portal' : 'onboarding');
      },
    });
  } else if (mode === 'settings') {
    renderSettings(learnRoot, () => setMode('learn-portal'));
  } else if (mode === 'hero-select') {
    // 主人翁系統 v1 — 純遊戲端選人畫面 (DOM)。選英雄 = 選門派玩法 + 戰車外觀,
    // 學習與遊戲分離鐵則: 0 答題彈窗。選完寫 selectedHeroId → 回 home (可重選)。
    renderHeroSelect(learnRoot, {
      onConfirm: () => setMode('home'),
      onBack: () => setMode('home'),
    });
  } else if (mode === 'weapon-select') {
    // R-005 P2 — 純遊戲端主武器選擇 + 升級畫面 (DOM)。選武器 = 選戰鬥玩法,
    // 升級 = 花武器素材變強。學習與遊戲分離鐵則: 0 答題彈窗、不碰修煉值。
    // 寫 selectedWeapon / weaponLevels / weaponShards → 返回 home (可重選/重升)。
    renderWeaponSelect(learnRoot, {
      onBack: () => setMode('home'),
    });
  }
}

// 通用「回首頁」事件 (戰鬥/ResultScene 結束等) → 落 learn-portal,讓學習先呈現。
window.addEventListener('ef:return-home', () => setMode('learn-portal'));
// W7 hub->battle wiring — ResultScene 「回 hub」 CTA dispatches this event so
// main.ts can sync its `currentMode` bookkeeping. ResultScene ALSO calls
// `scene.start('HubScene', { defeatedFaction })` directly so the
// markFactionConquered animation fires synchronously with the mode switch
// (this listener is the bookkeeping sync, not the scene transition itself).
window.addEventListener('ef:return-hub', () => setMode('hub'));
// boss 2026-06-13「第一場有技能按鈕,後面就不見了」:戰鬥場景轉結算時會
// remove('.touch-controls')(避免擋住結算 CTA);但「下一關/重試/選關」是 Phaser
// 內部 scene.start(不走 setMode)→ 不會重掛。各內部進戰鬥點 dispatch 此事件,
// 這裡只在按鈕「已被移除」時補掛(保留既有 unmount 句柄,避免 idempotent no-op 漂移)。
window.addEventListener('ef:enter-battle', () => {
  if (typeof document !== 'undefined' && !document.querySelector('.touch-controls')) {
    unmountTouch = mountTouchControls();
  }
});
window.addEventListener('ef:open-upload', () => setMode('photo-upload'));
window.addEventListener('ef:open-dashboard', () => setMode('dashboard'));
window.addEventListener('ef:open-boss-gallery', () => setMode('boss-gallery'));
window.addEventListener('ef:switch-profile', () => setMode('profile-picker'));
window.addEventListener('ef:open-academy-parent', () => setMode('academy-parent'));
window.addEventListener('ef:open-settings', () => setMode('settings'));
window.addEventListener('ef:open-review-hub', () => setMode('review-hub'));
// 主人翁系統 v1 — home 「選擇主角」 card fallback event.
window.addEventListener('ef:open-hero-select', () => setMode('hero-select'));
// R-005 P2 — home/loadout 「選擇武器」入口 fallback event.
window.addEventListener('ef:open-weapon-select', () => setMode('weapon-select'));

// 版本偵測:部署新版後,提示開著舊分頁的使用者更新(boss 2026-06-10「開著舊分頁
// 跑舊版」連續害他誤判功能壞掉)。只顯示橫幅,由使用者決定何時重整。
startVersionCheck();

// C05-wire (2026-05-23): home banner's 「教學魔王」 button → flip the next
// HorizontalBattleScene into tutorial-boss mode AND route the kid into the
// battle scene. The module-flag set by requestTutorialBossSpawn() is
// consumed inside the scene's create() so the very first boss spawn uses
// BOSS_TUTORIAL_POOL[0] (必勝 「練習魔王・木靶王」) instead of pickRandomBoss().
window.addEventListener('ef:start-tutorial-boss', () => {
  requestTutorialBossSpawn();
  setMode('h-battle');
});

// C05-wire (2026-05-23): scene's _bossDefeated() dispatches this when the
// tutorial boss dies. Persist the completion in PlayerProgress.dailyHook
// (idempotent — completeTutorialBoss returns false if already completed)
// so the home banner hides the 「教學魔王」 CTA and the snapshot reports
// `tutorialBossPending: false`. Grants TUTORIAL_BOSS_XP (200 freeXP) the
// first time only.
window.addEventListener('ef:tutorial-boss-completed', () => {
  const p = loadProgress();
  if (p) {
    try { completeTutorialBoss(p); } catch { /* defensive: never block result flow */ }
  }
});

// Boot: ensure family state, then show the DOM study cover (title) first.
// 封面「開始學習」按鈕 onStart → 多 profile 落 profile-picker,否則
// progress ? learn-portal : onboarding(學習先呈現)。
ensureFamilyState();
// 自我修復:無條件清掉「合併 bug」留下的「(本機)」垃圾 profile —— 不依賴雲端/登入,
// 純本機開啟也會清(之前綁在 initAccountSync 裡、雲端沒啟用就 return → 沒登入時洪水沒清)。
purgeJunkProfiles();
// 再併掉「同名重複帳」(無「(本機)」字樣的一堆「指揮官」;boss 2026-06-09「一樣喔」:
// 實測是同一份存檔被複製 10 份)。依實質進度指紋去重,內容真的不同的一律保留。
dedupeDuplicateProfiles();
// 雲端帳號同步 —— 未設定 Supabase env 時整個 no-op(向後相容,純本機 profile)。
// 登入時:雲端有料→拉回覆蓋本機(已過濾垃圾)→ 再清雲端垃圾列;並掛 saveProgress mirror。
initAccountSync();
const initialProgress = loadProgress();
if (initialProgress) {
  tickStreakOnLoad(initialProgress);
  saveProgress(initialProgress);
}

// ── iOS 風統一導覽:全域左緣滑動返回(掛一次)+「減少晃動」旗標 ──────────
// isActive = 學習 DOM 正顯示中(learnRoot 非 display:none)。遊戲模式時 learnRoot
// 被設 display:none → isActive 回 false → 滑動手勢直接放棄(硬規則:遊戲畫面不觸發)。
setLearnReduceMotion(isReduceMotion(initialProgress));
window.addEventListener('ef:reduce-motion-changed', (e) => {
  const detail = (e as CustomEvent<{ reduceMotion?: boolean }>).detail;
  setLearnReduceMotion(detail?.reduceMotion === true);
});
mountSwipeBack({
  isActive: () => learnRoot.style.display !== 'none',
  peekTarget: learnRoot,
});

setMode('title');
